import {
  Flex,
  Grid,
  IconSearchLine,
  ScreenReaderContent,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useCallback, useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import { useTranslation } from "react-i18next";
import { SubjectService } from "../../services/SubjectService";
import { UserService } from "../../services/UserService";
import { FeatureContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { MULTI, Selector } from "../Selectors/Selector";
import SettingsCell from "./SettingsCell";

const Users = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const features = useContext(FeatureContext);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [rows, setRows] = useState([]);
  const [sortedRows, setSortedRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sortColumns, setSortColumns] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [roleFilter, setRoleFilter] = useState("all");
  const [departmentsFilter, setDepartmentsFilter] = useState([]);

  const finalizeFlag =
    features.find((f) => f.name === "finalize_results_flag")?.value === "TRUE";

  let lastSearch = undefined;

  const roleSelections = finalizeFlag
    ? [
        { id: "all", label: t("user.allRoles") },
        { id: "ac", label: t("user.assessmentCoordinator") },
        { id: "cc", label: t("user.courseCoordinator") },
        { id: "faculty", label: t("user.faculty") },
        { id: "pc", label: t("user.programCoordinator") },
      ]
    : [
        { id: "all", label: t("user.allRoles") },
        { id: "ac", label: t("user.assessmentCoordinator") },
        { id: "faculty", label: t("user.faculty") },
        { id: "pc", label: t("user.programCoordinator") },
      ];

  const getRole = (roleId) => {
    switch (roleId) {
      case "ac":
        return t("user.assessmentCoordinator");
      case "cc":
        return t("user.courseCoordinator");
      case "faculty":
        return t("user.faculty");
      case "pc":
        return t("user.programCoordinator");
      default:
        return "";
    }
  };

  // function hasDepartmentIntersection(user) {
  //   return userDetails.ac_subjects.some((dept) =>
  //     user.ac_subjects.includes(dept)
  //   );
  // }

  // Fetch users and departments
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const [users, departments] = await Promise.all([
          UserService.getUsers(),
          SubjectService.getSubjects({ allSubjects: true }),
        ]);
        setUsers(users);
        buildRows(users);

        const selectOptions = departments.map((s) => ({
          id: s.title,
          label: s.name,
        }));
        setDepartments(selectOptions);
        setLoading(false);
      } catch {
        showNotification(t("notifications.user.errorFetchingUsers"), ERROR);
        setLoading(false);
        setError(true);
      }
    };
    fetchInfo();
  }, [showNotification]);

  // Build rows for the DataGrid given a list of users
  const buildRows = useCallback((users) => {
    const newRows = users.map((user, index) => {
      const fullName = user.name.trim().split(" ");
      const firstName = fullName.length > 1 ? fullName[0] : "";
      const lastName =
        fullName.length > 1 ? fullName.splice(1).join(" ") : fullName[0];
      return {
        index: index,
        canvasId: user.canvas_id,
        fullName: user.name,
        fname: firstName,
        lname: lastName,
        email: user.login_id ?? "",
        role: getRole(user.role_id),
        roleId: user.role_id,
        departments: user.ac_subjects,
        permissions: user.permissions,
      };
    });
    setRows(newRows);
  }, []);

  // Set columns for the DataGrid
  useEffect(() => {
    setColumns([
      { key: "fname", name: t("user.firstName"), sortable: true },
      { key: "lname", name: t("user.lastName"), sortable: true },
      { key: "email", name: t("user.username"), sortable: true },
      { key: "role", name: t("user.role"), sortable: true },
      {
        key: "settings",
        name: t("user.settings"),
        width: 80,
        renderCell: ({ row }) => (
          <SettingsCell
            row={row}
            departments={departments}
            setRows={setRows}
            getRole={getRole}
          />
        ),
      },
    ]);
  }, [departments, roleFilter, searchText]);

  const getComparator = (columnKey) => {
    switch (columnKey) {
      case "fname":
      case "lname":
      case "email":
      case "role":
        return (a, b) => {
          return a[columnKey].localeCompare(b[columnKey]);
        };
      default:
    }
  };

  useEffect(() => {
    const filteredRows = rows.filter((row) => {
      return row.fullName.toLowerCase().includes(searchText.toLowerCase());
    });

    if (sortColumns.length === 0) setSortedRows(filteredRows);

    let newRows = filteredRows.toSorted((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });

    if (departmentsFilter.length > 0) {
      newRows = newRows.filter((row) =>
        row.departments.some((dept) => departmentsFilter.includes(dept))
      );
    }

    switch (roleFilter) {
      case "ac":
        newRows = newRows.filter((row) => row.roleId === "ac");
        break;
      case "cc":
        newRows = newRows.filter((row) => row.roleId === "cc");
        break;
      case "faculty":
        newRows = newRows.filter((row) => row.roleId === "faculty");
        break;
      case "pc":
        newRows = newRows.filter((row) => row.roleId === "pc");
        break;
      default:
        break;
    }

    setSortedRows(newRows);
  }, [rows, sortColumns, searchText, roleFilter, departmentsFilter]);

  // Handle search input change
  // Debounce the search input to avoid too many re-renders
  const handleChange = (_e, value) => {
    window.clearTimeout(lastSearch);
    lastSearch = window.setTimeout(() => {
      setSearchText(value);
    }, 500);
  };

  return !loading ? (
    !error && (
      <Flex
        wrap="wrap"
        gap="small"
        height="calc(100vh - 2.375rem - 1.5rem - 3rem)"
      >
        <Flex.Item width="100%">
          <Grid vAlign="bottom" startAt="large">
            <Grid.Row>
              <Grid.Col width="auto">
                <Text>
                  {t("user.totalUsers")}
                  {users.length}
                </Text>
              </Grid.Col>
              <Grid.Col width={3}>
                <TextInput
                  id="user-search-input"
                  renderLabel={
                    <ScreenReaderContent>
                      {t("user.searchUsers")}
                    </ScreenReaderContent>
                  }
                  placeholder={t("user.searchUsers")}
                  size="small"
                  onChange={handleChange}
                  renderBeforeInput={<IconSearchLine />}
                />
              </Grid.Col>
              <Grid.Col width="auto">
                <Selector
                  id="role-filter"
                  label={t("user.role")}
                  options={roleSelections}
                  size="small"
                  selectedItemId={roleFilter}
                  selectHandler={(val) => setRoleFilter(val)}
                />
              </Grid.Col>
              <Grid.Col>
                <Selector
                  id="department-filter"
                  type={MULTI}
                  label={t("global.departments")}
                  options={departments}
                  size="small"
                  selectedOptions={departmentsFilter}
                  selectHandler={(val) => setDepartmentsFilter(val)}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Flex.Item>
        <Flex.Item width="100%" height={"calc(100% - 4rem)"}>
          <DataGrid
            className="rdg-light"
            style={{ overflow: "scroll", height: "100%" }}
            rowHeight={55}
            headerRowHeight={40}
            columns={columns}
            rows={sortedRows}
            sortColumns={sortColumns}
            onSortColumnsChange={(cols) => setSortColumns(cols.slice(-1))}
          />
        </Flex.Item>
      </Flex>
    )
  ) : (
    <Flex height="30rem" justifyItems="center">
      <Flex.Item textAlign="center">
        <Spinner renderTitle={t("user.loadingUserData")} size="large" />
        <Text weight="bold" size="large" as="div">
          {t("user.loadingUsers")}
        </Text>
      </Flex.Item>
    </Flex>
  );
};

export default Users;
